import React, { useState } from 'react';
import styled from 'styled-components';
import { LegalFooter } from './components/LegalFooter';
import { OtpCodeForm } from './components/OtpCodeForm';
import { Page } from './components/Page';
import { PageCard } from './components/PageCard';
import { PageCardLogo } from './components/PageCardLogo';
import { RegisterForm, RegisterFormValues } from './components/RegisterForm';
import { SplashPane } from './components/SplashPane';
import { useSendOtpMutation, useVerifyOtpMutation } from './hooks/mutations';
import { persistAttributionCookie } from './lib/attribution';

const readEmailFromUrl = () => {
    if (typeof window === 'undefined') return '';
    return new URLSearchParams(window.location.search).get('email') ?? '';
};

export const RegisterPage = () => {

    const [submitted, setSubmitted] = useState<RegisterFormValues | null>(null);
    const [code, setCode] = useState('');

    const sendMutation = useSendOtpMutation();
    const verifyMutation = useVerifyOtpMutation();

    const onSubmit = async (values: RegisterFormValues) => {
        persistAttributionCookie();
        await sendMutation.mutateAsync({ email: values.email });
        setSubmitted(values);
    };

    const onVerify = async () => {
        if (!submitted) return;
        // Pass `name` so the brand-new user is created with it (seeds the Principal),
        // matching how magic-link sign-up behaved.
        await verifyMutation.mutateAsync({ email: submitted.email, otp: code, name: submitted.name });
    };

    const onResend = async () => {
        if (!submitted) return;
        setCode('');
        verifyMutation.reset();
        await sendMutation.mutateAsync({ email: submitted.email });
    };

    return <Page>
        <PageCard splash={<SplashPane />}>
            <PageCardLogo />
            {submitted ? (
                <OtpCodeForm
                    email={submitted.email}
                    code={code}
                    onCodeChange={setCode}
                    onSubmit={() => void onVerify()}
                    onResend={() => void onResend()}
                    verifying={verifyMutation.isPending}
                    resending={sendMutation.isPending}
                    error={verifyMutation.error?.message ?? sendMutation.error?.message ?? null}
                />
            ) : (
                <>
                    <RegisterForm
                        initialEmail={readEmailFromUrl()}
                        onSubmit={values => void onSubmit(values)}
                        loading={sendMutation.isPending}
                    />
                    {sendMutation.error && <ErrorText>{sendMutation.error.message}</ErrorText>}
                </>
            )}
            <LegalFooter />
        </PageCard>
    </Page>;
};

const ErrorText = styled.p`
    font-size: 0.8125rem;
    color: #b91c1c;
    margin: 0.5rem 0 0;
`;
